import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Eye, Bell, BellOff } from 'lucide-react';
import { formatDate, formatDateTime } from '@/utils/dateFormat';

const statusLabels = { new: 'Neu', in_progress: 'In Bearbeitung', done: 'Erledigt', cancelled: 'Storniert' };
const statusColors = { new: 'border-green-500 text-green-600', in_progress: 'border-orange-500 text-orange-600', done: 'border-slate-300 text-slate-500', cancelled: 'border-red-500 text-red-600 bg-red-50' };

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);

  const load = useCallback(() => {
    const params = filter !== 'all' ? `?status=${filter}` : '';
    api.get(`/orders${params}`).then(r => setOrders(r.data)).catch(() => {});
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (orderId, status) => {
    try {
      await api.patch(`/orders/${orderId}/status`, { status });
      toast.success('Status aktualisiert');
      load();
      if (selectedOrder?.id === orderId) setSelectedOrder(prev => ({ ...prev, status }));
    } catch { toast.error('Fehler'); }
  };

  return (
    <div data-testid="admin-orders">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-[#0E518D]">Bestellungen</h1>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-48 rounded-sm" data-testid="order-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Status</SelectItem>
            <SelectItem value="new">Neu</SelectItem>
            <SelectItem value="in_progress">In Bearbeitung</SelectItem>
            <SelectItem value="done">Erledigt</SelectItem>
            <SelectItem value="cancelled">Storniert</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white rounded-sm shadow-sm border border-slate-100 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="text-xs uppercase tracking-wide">Nr.</TableHead>
              <TableHead className="text-xs uppercase tracking-wide">Gast</TableHead>
              <TableHead className="text-xs uppercase tracking-wide">Buchung</TableHead>
              <TableHead className="text-xs uppercase tracking-wide">Anreise</TableHead>
              <TableHead className="text-xs uppercase tracking-wide">Summe</TableHead>
              <TableHead className="text-xs uppercase tracking-wide">Lieferart</TableHead>
              <TableHead className="text-xs uppercase tracking-wide">Status</TableHead>
              <TableHead className="text-xs uppercase tracking-wide text-right">Aktionen</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orders.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-400">Keine Bestellungen</TableCell></TableRow>
            ) : orders.map(o => (
              <TableRow key={o.id} data-testid={`order-row-${o.id}`} className={o.status === 'cancelled' ? 'bg-red-50/50 opacity-75' : ''}>
                <TableCell className={`text-sm font-mono ${o.status === 'cancelled' ? 'line-through text-slate-400' : ''}`}>{o.order_number}</TableCell>
                <TableCell className={`text-sm ${o.status === 'cancelled' ? 'text-slate-400' : ''}`}>{o.first_name} {o.last_name}</TableCell>
                <TableCell className="text-sm">{o.booking_number}</TableCell>
                <TableCell className="text-sm">{formatDate(o.arrival_date)}</TableCell>
                <TableCell className="text-sm font-bold text-[#0E518D]">{o.total?.toFixed(2)} EUR</TableCell>
                <TableCell className="text-xs">{o.delivery_type === 'pickup' ? 'Abholung' : 'Wohnung'}</TableCell>
                <TableCell>
                  <Select value={o.status} onValueChange={v => updateStatus(o.id, v)}>
                    <SelectTrigger className={`w-36 h-8 rounded-sm text-xs ${statusColors[o.status] || ''}`} data-testid={`order-status-${o.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">Neu</SelectItem>
                      <SelectItem value="in_progress">In Bearbeitung</SelectItem>
                      <SelectItem value="done">Erledigt</SelectItem>
                      <SelectItem value="cancelled">Storniert</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => setSelectedOrder(o)} data-testid={`view-order-${o.id}`}><Eye className="h-3.5 w-3.5" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!selectedOrder} onOpenChange={() => setSelectedOrder(null)}>
        <DialogContent className="bg-white max-w-lg" data-testid="order-detail-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#0E518D]">Bestellung {selectedOrder?.order_number}</DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <div className="mt-4 space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-slate-400">Name:</span><p className="font-medium">{selectedOrder.first_name} {selectedOrder.last_name}</p></div>
                <div><span className="text-slate-400">E-Mail:</span><p className="font-medium">{selectedOrder.email}</p></div>
                <div><span className="text-slate-400">Buchungsnr.:</span><p className="font-medium">{selectedOrder.booking_number}</p></div>
                <div><span className="text-slate-400">Anreise:</span><p className="font-medium">{formatDate(selectedOrder.arrival_date)}</p></div>
                <div><span className="text-slate-400">Lieferart:</span><p className="font-medium">{selectedOrder.delivery_type === 'pickup' ? 'Abholung' : 'Wohnung'}</p></div>
                <div><span className="text-slate-400">Telefon:</span><p className="font-medium">{selectedOrder.phone || '-'}</p></div>
              </div>
              
              {/* Erinnerungs-Status */}
              <div className={`flex items-center gap-2 p-3 rounded-sm text-sm ${selectedOrder.reminder_sent ? 'bg-green-50 border border-green-200' : 'bg-slate-50 border border-slate-200'}`} data-testid="order-reminder-status">
                {selectedOrder.reminder_sent ? (
                  <>
                    <Bell className="h-4 w-4 text-green-600" />
                    <span className="text-green-700">Erinnerung gesendet</span>
                    {selectedOrder.reminder_sent_at && (
                      <span className="text-green-600 text-xs ml-auto">
                        {formatDateTime(selectedOrder.reminder_sent_at)}
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    <BellOff className="h-4 w-4 text-slate-400" />
                    <span className="text-slate-500">Erinnerung noch nicht gesendet</span>
                  </>
                )}
              </div>
              
              {selectedOrder.message && <div className="text-sm"><span className="text-slate-400">Nachricht:</span><p className="mt-1 bg-slate-50 p-3 rounded-sm">{selectedOrder.message}</p></div>}
              <Separator />
              <div>
                <p className="text-sm font-semibold mb-2">Produkte</p>
                {selectedOrder.items?.map((item, i) => (
                  <div key={i} className="flex justify-between text-sm bg-slate-50 p-2 rounded-sm mb-1">
                    <span>{item.name} <span className="text-slate-400">(Art. {item.article_number})</span> x {item.quantity}</span>
                    <span className="font-bold text-[#0E518D]">{item.total?.toFixed(2)} €</span>
                  </div>
                ))}
              </div>
              
              {/* Summenaufschlüsselung */}
              <div className="bg-slate-50 p-3 rounded-sm space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Produktsumme</span>
                  <span>{(selectedOrder.subtotal_products ?? selectedOrder.total ?? 0).toFixed(2)} €</span>
                </div>
                {(selectedOrder.service_fee != null && selectedOrder.service_fee > 0) && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Liefer- und Servicepauschale</span>
                    <span>{selectedOrder.service_fee.toFixed(2)} €</span>
                  </div>
                )}
                <Separator />
                <div className="flex justify-between items-center">
                  <span className="font-semibold">Gesamtsumme</span>
                  <span className="text-xl font-bold text-[#0E518D]">{selectedOrder.total?.toFixed(2)} €</span>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
